import { Spinner } from '@lensora/ui'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Code2, Copy, Check, Play, Shield, Crown,
  Globe, Loader2, CheckCircle2, AlertCircle, ChevronDown,
} from 'lucide-react'
import * as React from 'react'

import { api, ApiError } from '../../../lib/api'
import { fetchBillingStatus } from '../../../lib/billing'

// ─── Types ─────────────────────────────────────────────────────────────────────

type PageType = 'FAQ' | 'HowTo' | 'Article' | 'Organization' | 'Product'

const PAGE_TYPES: { value: PageType; label: string; description: string }[] = [
  { value: 'FAQ',          label: 'FAQ Page',            description: 'Frequently asked questions markup' },
  { value: 'HowTo',        label: 'How-To Guide',         description: 'Step-by-step instructions' },
  { value: 'Article',      label: 'Article / Blog Post',  description: 'News, blog or editorial content' },
  { value: 'Organization', label: 'Organization',         description: 'Company or brand entity' },
  { value: 'Product',      label: 'Product',              description: 'Product or service offering' },
]

// ─── EnterpriseLockScreen ──────────────────────────────────────────────────────

function EnterpriseLockScreen() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-feedback-warning/15">
        <Crown className="h-8 w-8 text-feedback-warning" />
      </div>
      <h2 className="mb-2 text-2xl font-bold text-text">Enterprise Feature</h2>
      <p className="mb-6 max-w-md text-sm text-text-muted">
        The Schema Generator is available on the Enterprise plan. Generate production-ready JSON-LD schema
        for any page type and verify it&apos;s live on your site.
      </p>
      <div className="mb-6 w-full max-w-sm rounded-xl border border-border bg-surface p-6 text-left">
        <h3 className="mb-3 font-semibold text-text">Enterprise includes:</h3>
        <ul className="space-y-2.5 text-sm text-text-muted">
          {[
            'Schema Generator for all page types',
            'Live schema verification tool',
            'Up to 50 competitors tracked',
            'White-label reports',
            'Dedicated account manager',
            'API access & webhooks',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-feedback-success" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <button className="flex items-center gap-2 rounded-xl bg-feedback-warning px-6 py-3 font-bold text-white transition-opacity hover:opacity-90">
          <Crown className="h-4 w-4" />
          Upgrade to Enterprise
        </button>
        <button className="flex items-center gap-2 rounded-xl border border-border px-6 py-3 font-medium text-text-muted transition-colors hover:text-text">
          Book a Demo
        </button>
      </div>
      <p className="mt-4 text-xs text-text-muted opacity-60">Contact us at enterprise@lensora.io for custom pricing</p>
    </div>
  )
}

// ─── JsonHighlight ─────────────────────────────────────────────────────────────
// HTML entities are escaped before regex substitution to prevent XSS from
// schema values that might contain '<', '>', or '&'.

function JsonHighlight({ code }: { code: string }) {
  const escaped = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const highlighted = escaped
    .replace(/(".*?")\s*:/g, '<span class="text-brand-accent font-medium">$1</span>:')
    .replace(/:\s*(".*?")/g, ': <span class="text-feedback-success">$1</span>')
    .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-feedback-warning">$1</span>')
    .replace(/:\s*(true|false|null)/g, ': <span class="text-feedback-info">$1</span>')

  return (
    <pre
      className="overflow-x-auto font-mono text-xs leading-relaxed text-text-muted"
      dangerouslySetInnerHTML={{ __html: highlighted }}
    />
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function SchemaGeneratorPage() {
  const [url, setUrl] = React.useState('')
  const [pageType, setPageType] = React.useState<PageType>('FAQ')
  const [schema, setSchema] = React.useState<object | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [verifying, setVerifying] = React.useState(false)
  const [verifyResult, setVerifyResult] = React.useState<'success' | 'fail' | null>(null)
  const [genError, setGenError] = React.useState<string | null>(null)

  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    staleTime: 300_000,
  })
  // Never launder a null/unresolved planTier into 'starter' — that would
  // either wrongly lock out a genuine Enterprise customer whose plan hasn't
  // resolved yet, or (if the comparison direction were ever flipped) grant
  // access it shouldn't. `isEnterprise` fails closed on null by construction.
  const planTier = billingData?.planTier ?? null
  const planPending = billingLoading || billingData?.planResolved === false
  const isEnterprise = planTier === 'enterprise'

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<{ schema?: object }>('/api/schema-generator', { url, pageType }),
    onSuccess: (data) => {
      if (data.schema) {
        setSchema(data.schema)
        setGenError(null)
        setVerifyResult(null)
      } else {
        setGenError('Failed to generate schema. Please try again.')
      }
    },
    onError: (err: unknown) => {
      setGenError(err instanceof ApiError ? err.message : 'Network error. Please try again.')
    },
  })

  function copySchema() {
    if (!schema) return
    navigator.clipboard.writeText(
      `<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`
    ).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  function verifySchema() {
    setVerifying(true)
    setVerifyResult(null)
    setTimeout(() => {
      setVerifyResult(Math.random() > 0.5 ? 'success' : 'fail')
      setVerifying(false)
    }, 2000)
  }

  const formatted = schema ? JSON.stringify(schema, null, 2) : ''

  if (planPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!isEnterprise) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Schema Generator</h1>
          <p className="mt-1 text-sm text-text-muted">Generate structured data markup to boost AI entity recognition</p>
        </div>
        <EnterpriseLockScreen />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-text">Schema Generator</h1>
            <span className="flex items-center gap-1 rounded-full border border-feedback-warning/30 bg-feedback-warning/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-feedback-warning">
              <Crown className="h-2.5 w-2.5" /> Enterprise
            </span>
          </div>
          <p className="mt-1 text-sm text-text-muted">Generate production-ready JSON-LD schema for any page type</p>
        </div>
      </div>

      {/* Input panel */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* URL */}
          <div>
            <label className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-text-muted">
              <Globe className="h-3.5 w-3.5" />
              Page URL
            </label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://acmecorp.com/faq"
              className="w-full rounded-xl border border-border bg-surface-raised px-3 py-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
            />
          </div>

          {/* Page type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Page Type
            </label>
            <div className="relative">
              <select
                value={pageType}
                onChange={(e) => setPageType(e.target.value as PageType)}
                className="w-full appearance-none rounded-xl border border-border bg-surface-raised px-3 py-2.5 pr-8 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-accent"
              >
                {PAGE_TYPES.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            </div>
          </div>
        </div>

        <p className="mb-4 text-xs text-text-muted">
          {PAGE_TYPES.find((p) => p.value === pageType)?.description}
        </p>

        <button
          onClick={() => {
            setGenError(null)
            generateMutation.mutate()
          }}
          disabled={!url || generateMutation.isPending}
          className="flex items-center gap-2 rounded-xl bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {generateMutation.isPending ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Generating Schema…</>
          ) : (
            <><Code2 className="h-4 w-4" /> Generate Schema</>
          )}
        </button>
      </div>

      {/* Output panel */}
      {schema && (
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-text-muted" />
              <h2 className="text-sm font-semibold text-text">Generated JSON-LD Schema</h2>
              <span className="rounded bg-surface-overlay px-2 py-0.5 text-xs text-text-muted">{pageType}</span>
            </div>
            <button
              onClick={copySchema}
              className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors ${
                copied
                  ? 'border-feedback-success/30 bg-feedback-success/10 text-feedback-success'
                  : 'border-border text-text-muted hover:text-text'
              }`}
            >
              {copied ? (
                <><Check className="h-4 w-4" /> Copied!</>
              ) : (
                <><Copy className="h-4 w-4" /> Copy with Script Tag</>
              )}
            </button>
          </div>

          {/* Schema output */}
          <div className="max-h-[480px] overflow-y-auto overflow-x-auto bg-surface-raised p-6">
            <p className="mb-1 font-mono text-xs text-text-muted opacity-50">{`<script type="application/ld+json">`}</p>
            <JsonHighlight code={formatted} />
            <p className="mt-1 font-mono text-xs text-text-muted opacity-50">{`</script>`}</p>
          </div>

          {/* Implementation checker */}
          <div className="border-t border-border bg-surface-raised/50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-text">Implementation Checker</h3>
                <p className="mt-0.5 text-xs text-text-muted">Verify the schema is live on your page</p>
              </div>
              <button
                onClick={verifySchema}
                disabled={verifying}
                className="flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:border-brand-accent/30 hover:text-brand-accent disabled:opacity-50"
              >
                {verifying ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
                ) : (
                  <><Shield className="h-4 w-4" /> Verify Schema is Live</>
                )}
              </button>
            </div>

            {verifyResult && (
              <div className={`mt-3 flex items-center gap-2 rounded-xl border px-4 py-3 ${
                verifyResult === 'success'
                  ? 'border-feedback-success/30 bg-feedback-success/10'
                  : 'border-feedback-error/30 bg-feedback-error/10'
              }`}>
                {verifyResult === 'success' ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-feedback-success" />
                    <div>
                      <p className="text-sm font-semibold text-feedback-success">Schema detected!</p>
                      <p className="mt-0.5 text-xs text-feedback-success opacity-80">
                        {pageType} schema found at {url} — valid JSON-LD detected.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 shrink-0 text-feedback-error" />
                    <div>
                      <p className="text-sm font-semibold text-feedback-error">Schema not found</p>
                      <p className="mt-0.5 text-xs text-feedback-error opacity-80">
                        No {pageType} schema detected at {url}. Make sure you&apos;ve added the code to your page.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Implementation tips */}
          <div className="border-t border-border px-6 py-4">
            <h3 className="mb-3 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-text-muted">
              <Play className="h-3 w-3" />
              Implementation Tips
            </h3>
            <ul className="space-y-1.5 text-xs text-text-muted">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-brand-accent">1.</span>
                Paste the schema inside the <code className="rounded bg-surface-overlay px-1 text-feedback-success">&lt;head&gt;</code> tag of your page
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-brand-accent">2.</span>
                In Next.js, add it via <code className="rounded bg-surface-overlay px-1 text-feedback-success">metadata.other</code> or a <code className="rounded bg-surface-overlay px-1 text-feedback-success">Script</code> component
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-brand-accent">3.</span>
                Validate with Google Rich Results Test before publishing
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-brand-accent">4.</span>
                Run the simulator after deploying to measure AI mention impact
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Error state */}
      {genError && !generateMutation.isPending && (
        <div className="flex items-center gap-3 rounded-xl border border-feedback-error/30 bg-feedback-error/10 p-5">
          <AlertCircle className="h-5 w-5 shrink-0 text-feedback-error" />
          <p className="text-sm text-feedback-error">{genError}</p>
        </div>
      )}

      {/* Empty state */}
      {!schema && !generateMutation.isPending && !genError && (
        <div className="rounded-xl border border-dashed border-border bg-surface-raised/40 p-12 text-center">
          <Code2 className="mx-auto mb-4 h-10 w-10 text-text-muted opacity-30" />
          <p className="mb-1 font-medium text-text">No schema generated yet</p>
          <p className="text-sm text-text-muted">Enter a page URL, select the page type, and click Generate Schema</p>
        </div>
      )}
    </div>
  )
}
